/**
 * /online-programmes/ankle/finder — Ankle Programme Finder (one question per
 * screen, server-side branching + routing). Also serves:
 *   ?assignment=<id>  patient completing an assigned form
 *   ?patient=<id>     clinician entering a walk-in on the patient's behalf
 *   ?claim=1          returning after register/login to bind a guest result
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { PainMarkerMap, OttawaMap, VIEWS, MAP_VERSION } from "@/components/ankle/AnkleMaps";
import { CommentBox } from "@/components/ankle/CommentBox";
import { AnkleResult } from "@/components/ankle/AnkleResult";

function condTrue(cond, answers) {
  if (cond.any) return cond.any.some((c) => condTrue(c, answers));
  if (cond.all) return cond.all.every((c) => condTrue(c, answers));
  const g = answers[cond.question_id];
  const vals = Array.isArray(g) ? g : [g];
  return vals.some((v) => cond.in.includes(v));
}
const applicable = (qs, answers) => qs.filter((q) => !q.show_if || condTrue(q.show_if, answers));

export default function AnkleFinder() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const assignmentId = params.get("assignment");
  const walkInPatient = params.get("patient");
  const [def, setDef] = useState(null);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [confirmed, setConfirmed] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [mainMarker, setMainMarker] = useState(null);
  const [regionHint, setRegionHint] = useState(false);
  const [step, setStep] = useState(0);
  const [guest, setGuest] = useState({ email: "", name: "" });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ankle-finder/definition").then((r) => setDef(r.data)).catch(() => setError("Could not load the questionnaire."));
  }, []);

  // Returning from register/login with a pending guest result → claim it.
  useEffect(() => {
    if (!user || params.get("claim") !== "1") return;
    const raw = sessionStorage.getItem("ankle_pending_claim");
    if (!raw) return;
    const pending = JSON.parse(raw);
    api.post("/ankle-finder/responses/claim", { response_id: pending.response_id, token: pending.token })
      .then((r) => { sessionStorage.removeItem("ankle_pending_claim"); setResult(r.data); })
      .catch((e) => setError(e?.response?.data?.detail || "We couldn't link your result to this account — please screen again."));
  }, [user, params]);

  const questions = def?.questions || [];
  const painOptions = questions.find((x) => x.id === "q4_character")?.options || [];
  const visible = useMemo(() => applicable(questions, answers), [questions, answers]);
  const q = visible[step];
  const isStaff = user && user.role !== "patient";

  // Marker → main region proposal (visible, patient confirms via the text options).
  const chooseMain = (id) => {
    setMainMarker(id);
    if (id === "none") { setAnswer("q3_location", "diffuse"); setRegionHint(true); return; }
    const m = markers.find((x) => x.id === id);
    const region = VIEWS.find((v) => v.id === m?.view)?.region;
    if (region) { setAnswer("q3_location", region); setRegionHint(true); }
  };
  useEffect(() => {
    if (markers.length === 1 && mainMarker !== markers[0].id) chooseMain(markers[0].id);
    if (markers.length > 1 && mainMarker && mainMarker !== "none" && !markers.some((m) => m.id === mainMarker)) setMainMarker(null);
    if (markers.length === 0 && mainMarker) { setMainMarker(null); setRegionHint(false); }
    // eslint-disable-next-line
  }, [markers]);
  // Pain-type asked once per location: prefill q4 from the main marker when unanswered.
  useEffect(() => {
    if (q?.id !== "q4_character" || answers.q4_character) return;
    const m = markers.find((x) => x.id === mainMarker);
    if (m?.types?.length) setAnswers((a) => ({ ...a, q4_character: [...m.types] }));
    // eslint-disable-next-line
  }, [q?.id]);

  const setAnswer = (qid, val) => {
    setAnswers((a) => {
      const next = { ...a, [qid]: val };
      const keep = new Set(applicable(questions, next).map((x) => x.id));
      Object.keys(next).forEach((k) => { if (!keep.has(k)) delete next[k]; });
      return next;
    });
    setResult(null);
  };
  const toggleMulti = (qq, oid) => {
    const cur = Array.isArray(answers[qq.id]) ? answers[qq.id] : [];
    const excl = Array.isArray(qq.exclusive_option) ? qq.exclusive_option : qq.exclusive_option ? [qq.exclusive_option] : [];
    let next;
    if (cur.includes(oid)) next = cur.filter((x) => x !== oid);
    else if (excl.includes(oid)) next = [oid];
    else next = [...cur.filter((x) => !excl.includes(x)), oid];
    if (qq.max_select && next.length > qq.max_select) next = next.slice(next.length - qq.max_select);
    setAnswer(qq.id, next);
  };
  const confirmSuggestion = (s) => {
    const target = questions.find((x) => x.id === s.question_id);
    if (!target || target.safety) return;
    if (target.kind === "multi") {
      const cur = Array.isArray(answers[target.id]) ? answers[target.id] : [];
      if (!cur.includes(s.option_id)) toggleMulti(target, s.option_id);
    } else setAnswer(target.id, s.option_id);
    setConfirmed((c) => [...c, { question_id: s.question_id, option_id: s.option_id, matched: s.matched }]);
  };

  const answered = (qq) => {
    const g = answers[qq.id];
    if (qq.kind === "multi") return Array.isArray(g) && g.length > 0 && (!qq.require_all || g.length === qq.options.length);
    return typeof g === "string" && g.length > 0;
  };
  const isLast = step === visible.length - 1;
  const needGuest = !user && isLast;
  const guestOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email);

  const submit = async () => {
    // Actionable client validation — jump to the first problem instead of a dead button.
    const firstMissing = visible.find((qq) => !answered(qq));
    if (firstMissing) {
      setStep(visible.indexOf(firstMissing));
      setError(`Please answer: "${firstMissing.label}"`);
      return;
    }
    if (markers.length > 1 && !mainMarker) { setStep(visible.findIndex((x) => x.ui === "pain_map")); setError("Please tell us which marker is your main concern (or choose \"No single main area\")."); return; }
    if (!user && !guestOk) { setError("Please enter a valid email so we can save your result."); return; }
    setBusy(true); setError(null);
    try {
      const body = { version: def.version, answers, comments, confirmed_suggestions: confirmed,
        pain_markers: markers, main_marker: markers.length ? (mainMarker || markers[0].id) : null, map_version: MAP_VERSION };
      if (assignmentId) body.assignment_id = assignmentId;
      if (walkInPatient && isStaff) body.patient_id = walkInPatient;
      if (!user) { body.guest_email = guest.email.trim(); body.guest_name = guest.name.trim(); }
      const r = await api.post("/ankle-finder/submit", body);
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (!e?.response) setError("We couldn't reach the server — please check your connection and press \"See my result\" again. Your answers are kept.");
      else if (d?.problems) {
        const firstBad = visible.findIndex((x) => d.problems.some((p) => p.question_id === x.id));
        if (firstBad >= 0) setStep(firstBad);
        setError(d.message);
      } else setError(typeof d === "string" ? d : (d?.message || "Something went wrong on our side — please try again. Your answers are kept."));
    } finally { setBusy(false); }
  };

  const restart = () => { setAnswers({}); setComments({}); setConfirmed([]); setMarkers([]); setMainMarker(null); setRegionHint(false); setStep(0); setResult(null); setError(null); };
  const progress = visible.length ? Math.round(((step + (result ? 1 : 0)) / visible.length) * 100) : 0;

  return (
    <div className="fl-page min-h-screen" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />
      <section className="pt-28 md:pt-36 pb-6 px-6 bg-[#0A0A0A] fl-noise">
        <div className="max-w-2xl mx-auto">
          <Link to="/online-programmes/ankle" className="text-xs text-[#f47a20] hover:underline" data-testid="finder-back-landing">← Ankle programmes</Link>
          <div className="fl-label text-[#f47a20] mt-4">Ankle Programme Finder</div>
          <h1 className="fl-heading text-3xl md:text-4xl mt-2" data-testid="finder-heading">Find your best next step</h1>
          <p className="text-sm mt-2 text-zinc-400">About 5 minutes. Safety first, then only the questions relevant to you. This is a programme-matching tool, not a diagnosis.</p>
          {walkInPatient && isStaff && <div className="mt-3 text-xs px-3 py-2 rounded-md bg-[#f47a20]/10 border border-[#f47a20]/40 text-[#f47a20]" data-testid="finder-walkin-banner">Clinician mode — answers are recorded as entered on the patient's behalf.</div>}
          {!result && (
            <div className="mt-5" aria-label="Progress">
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#f47a20] transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
              <div className="text-[11px] text-zinc-500 mt-1" data-testid="finder-progress">Question {Math.min(step + 1, visible.length)} of {visible.length}{visible.length < questions.length ? " (only questions that apply to you)" : ""}</div>
            </div>
          )}
        </div>
      </section>

      <section className="py-8 px-6 bg-[#121212] border-t border-white/5 min-h-[420px]">
        <div className="max-w-2xl mx-auto">
          {error && <div className="fl-card p-4 text-sm text-red-300 border border-red-500/40 mb-4" role="alert" data-testid="finder-error">{error}</div>}
          {!def && !error && <div className="fl-card p-8 text-center text-sm text-zinc-400"><Loader2 className="animate-spin mx-auto mb-2" /> Loading…</div>}
          {result && <AnkleResult result={result} user={user} guest={guest} onRestart={restart} />}
          {def && !result && q && (
            <div className="fl-card p-5 md:p-7" data-testid={`finder-q-${q.id}`} key={q.id}>
              <div className="fl-label text-[#f47a20]">{q.stage}{q.draft ? " · draft question" : ""}</div>
              <h2 className="text-lg md:text-xl mt-2 leading-snug" id={`label-${q.id}`}>{q.label}</h2>
              {q.help && <p className="text-xs text-zinc-400 mt-1">{q.help}</p>}
              {q.kind === "multi" && <p className="text-[11px] text-zinc-500 mt-1">{q.max_select ? `Choose up to ${q.max_select}` : q.require_all ? "Please confirm each statement" : "Select all that apply"}</p>}

              {q.ui === "pain_map" && (
                <div className="mt-4 space-y-4">
                  <PainMarkerMap markers={markers} onChange={(m) => { setMarkers(m); setResult(null); }} painOptions={painOptions} maxTypes={2} />
                  {markers.length > 1 && (
                    <div data-testid="main-marker-picker">
                      <div className="text-sm">Which marker is your main concern?</div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {markers.map((m, i) => (
                          <button key={m.id} type="button" onClick={() => chooseMain(m.id)} data-testid={`main-marker-${i + 1}`}
                            className={`text-xs px-3 py-1.5 rounded border ${mainMarker === m.id ? "bg-[#f47a20] text-black border-[#f47a20]" : "border-white/15 text-zinc-300"}`}>Marker {i + 1}</button>
                        ))}
                        <button type="button" onClick={() => chooseMain("none")} data-testid="main-marker-none"
                          className={`text-xs px-3 py-1.5 rounded border ${mainMarker === "none" ? "bg-[#f47a20] text-black border-[#f47a20]" : "border-white/15 text-zinc-300"}`}>No single main area</button>
                      </div>
                    </div>
                  )}
                  <div className="text-sm pt-2 border-t border-white/10">Confirm your main pain region{regionHint && <span className="text-xs text-zinc-400"> — suggested from your marker; change it if needed</span>}. Choose "Plantar heel / midfoot" for pain underneath the foot (not shown in the pictures).</div>
                </div>
              )}
              {q.ui === "bone_map" && <div className="mt-4"><OttawaMap value={answers[q.id] || []} onToggle={(v) => toggleMulti(q, v)} /></div>}

              <div className="mt-4 grid gap-2" role={q.kind === "multi" ? "group" : "radiogroup"} aria-labelledby={`label-${q.id}`}>
                {q.options.map((o) => {
                  const active = q.kind === "multi" ? (answers[q.id] || []).includes(o.id) : answers[q.id] === o.id;
                  return (
                    <button
                      key={o.id} type="button"
                      role={q.kind === "multi" ? "checkbox" : "radio"} aria-checked={active}
                      onClick={() => (q.kind === "multi" ? toggleMulti(q, o.id) : setAnswer(q.id, o.id))}
                      className={`text-left rounded-lg border px-4 py-3 text-sm transition-[background-color,border-color,transform] duration-150 active:scale-[0.99] ${active ? "border-[#f47a20] bg-[#f47a20]/15 text-white" : "border-white/10 bg-white/[0.02] hover:border-[#f47a20]/60 text-zinc-200"}`}
                      data-testid={`finder-opt-${q.id}-${o.id}`}
                    >
                      <span className="flex items-start gap-3">
                        <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-${q.kind === "multi" ? "sm" : "full"} border flex items-center justify-center ${active ? "border-[#f47a20] bg-[#f47a20]" : "border-white/30"}`}>{active && <Check size={11} className="text-black" />}</span>
                        <span><span>{o.label}</span>{o.sub && <span className="block text-xs text-zinc-400 mt-0.5">{o.sub}</span>}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {q.allow_comment && (
                <CommentBox qid={q.id} value={comments[q.id]} onChange={(v) => setComments((c) => ({ ...c, [q.id]: v }))} questions={questions} onConfirmSuggestion={confirmSuggestion} />
              )}

              {needGuest && (
                <div className="mt-6 pt-5 border-t border-white/10" data-testid="finder-guest-details">
                  <div className="text-sm font-medium">Where should we send your result?</div>
                  <p className="text-xs text-zinc-500 mt-1">No account needed. We use your email to keep your result and, if a programme is available, to link it to your account when you continue.</p>
                  <div className="grid sm:grid-cols-2 gap-2 mt-3">
                    <input className="fl-input text-sm" placeholder="Full name" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} data-testid="finder-guest-name" />
                    <input className="fl-input text-sm" type="email" placeholder="Email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} data-testid="finder-guest-email" />
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-center justify-between gap-3">
                <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="fl-btn-ghost text-sm disabled:opacity-40" data-testid="finder-back"><ArrowLeft size={14} /> Back</button>
                {isLast ? (
                  <button type="button" onClick={submit} disabled={busy} aria-busy={busy} className="fl-btn text-sm disabled:opacity-60" data-testid="finder-submit">{busy ? <><Loader2 size={14} className="animate-spin" /> Checking your answers…</> : <>See my result <ArrowRight size={14} /></>}</button>
                ) : (
                  <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!answered(q)} className="fl-btn text-sm disabled:opacity-40" data-testid="finder-next">Next <ArrowRight size={14} /></button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
